import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'

const SectionItem = ({
  section,
  selectedQuestions,
  onSectionSelect,
  onQuestionSelect,
  onSelectAllQuestions,
  onDeselectAllQuestions,
  getSectionSelectedCount
}) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const handleToggle = useCallback(event => {
    event.stopPropagation()
    setIsExpanded(prev => !prev)
  }, [])

  const handleSectionSelect = useCallback(event => {
    event.stopPropagation()
    onSectionSelect(section.id)
  }, [section.id, onSectionSelect])

  const handleQuestionSelect = useCallback(questionId => event => {
    event.stopPropagation()
    onQuestionSelect(section.id, questionId)
  }, [section.id, onQuestionSelect])

  const handleSelectAll = useCallback(event => {
    event.preventDefault()
    onSelectAllQuestions(section.id)
  }, [section.id, onSelectAllQuestions])

  const handleDeselectAll = useCallback(event => {
    event.preventDefault()
    onDeselectAllQuestions(section.id)
  }, [section.id, onDeselectAllQuestions])

  const selectedCount = getSectionSelectedCount(section.id)
  const isChecked = selectedCount > 0
  const sectionQuestions = selectedQuestions[section.id] || []

  return (
    <li className="list-group-item p-0">
      <div className="d-flex align-items-center justify-content-between w-100 p-3">
        <div
          className="d-flex align-items-center"
          style={{ cursor: 'pointer' }}
          onClick={handleSectionSelect}
        >
          <div className="custom-control custom-checkbox">
            <input
              type="checkbox"
              className="custom-control-input"
              id={`section-${section.id}`}
              data-testid={`section-${section.id}-checkbox`}
              checked={isChecked}
              onChange={handleSectionSelect}
            />
            <label className="custom-control-label" />
          </div>
          <span className="font-weight-bold h5 m-0">{section.name}</span>
          <span
            className="badge bg-light ml-2 p-2 border rounded
              d-inline-flex align-items-center justify-content-center badge-square"
          >
            <span className="font-weight-bolder text-dark h6 m-0">
              {selectedCount}
            </span>
          </span>
        </div>
        <button
          type="button"
          className="btn btn-link p-0"
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
          onClick={handleToggle}
        >
          <i
            className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}
            data-testid={`chevron-${section.id}`}
          />
        </button>
      </div>
      {isExpanded ? (
        <div className="bg-light border-top">
          <div className="d-flex justify-content-between align-items-center">
            <div className="px-4 py-3 d-flex align-items-center">
              <strong>Questions :</strong>
              <div className="d-flex align-items-center ml-1">
                <button
                  type="button"
                  className="btn btn-link p-0"
                  onClick={handleSelectAll}
                >
                  Tout cocher
                </button>
                <span className="mx-1">/</span>
                <button
                  type="button"
                  className="btn btn-link p-0"
                  onClick={handleDeselectAll}
                >
                  Tout décocher
                </button>
              </div>
            </div>
          </div>
          {section.questions?.map(question => (
            <div
              key={question.id}
              className="d-flex questions align-items-center border-top bg-light px-4 py-3"
              role="button"
              tabIndex={0}
              style={{ cursor: 'pointer' }}
              onClick={handleQuestionSelect(question.id)}
            >
              <div className="custom-control custom-checkbox flex-grow-1">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id={`question-${section.id}-${question.id}`}
                  checked={sectionQuestions.includes(question.id)}
                  readOnly
                />
                <label
                  className="custom-control-label text-body user-select-none"
                >
                  {question.title}
                </label>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </li>
  )
}

SectionItem.propTypes = {
  getSectionSelectedCount: PropTypes.func.isRequired,
  onDeselectAllQuestions: PropTypes.func.isRequired,
  onQuestionSelect: PropTypes.func.isRequired,
  onSectionSelect: PropTypes.func.isRequired,
  onSelectAllQuestions: PropTypes.func.isRequired,
  section: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    questions: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired
    }))
  }).isRequired,
  selectedQuestions: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.number)).isRequired
}

export default SectionItem